import { styled, css, Button } from 'canoe';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import EventListener from 'react-event-listener';
import { colors, Menu as StyleguideMenu, FlexRow } from 'styleguide';

import { hasVariablesAccess, useHasPermission } from 'commons/auths';
import { useFlags, Flags } from 'commons/hooks';
import { hotkeys } from 'commons/hotkeys';
import { Input, LinkInputContainer } from 'craft/components/SelectedToolbar/PopoverForm';
import { useUserTraits } from 'craft/components/VariableEditor/useUserTraits';
import { LinkDefinition, LinkVariable } from 'craft/rte/plugins/withLink/wrapLink';
import { parseLink } from 'craft/utils/parseLink';

import { ManageVariables } from './components/ManageVariables';

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 16px;
  justify-content: flex-end;
`;

const Menu = styled(StyleguideMenu)`
  ${({ theme }) => css`
    padding: ${theme.spaces.s};
    border-radius: ${theme.borderRadius.small};
    display: flex;
    align-items: center;
    flex-direction: column;
    overflow: visible;
    background-color: ${theme.colors.OnyxGrayD10};
  `}
`;

const InputArea = styled(FlexRow)<any>`
  width: 100%;
`;

const MoreButton = styled.button`
  color: ${colors('white')};
  border: none;
  background: transparent;
  text-decoration: underline;
  font-size: 12px;
`;

interface LinkToolbarProps {
  onClose: () => void;
  onLink: (link: LinkDefinition) => void;
  onUnlink: () => void;
  initialValue: LinkDefinition;
}

export const LinkToolbar = ({ onClose, onLink, onUnlink, initialValue }: LinkToolbarProps) => {
  /**
   * Variables within links are feature flagged only for specific accounts for the time being
   * Opting in to this will disable the link "autocomplete"
   */
  const { [Flags.LINK_VARIABLES]: withLinkVariables } = useFlags();

  const [value, setValue] = useState(initialValue.href || '');
  const [showingMore, setShowingMore] = useState(false);
  const traits = useUserTraits();
  const canVariableAccess = useHasPermission(hasVariablesAccess);
  const [linkVariables, setLinkVariables] = useState(initialValue.variables || []);

  const hasLinkVariables = withLinkVariables || linkVariables.length > 0;

  const inputRef = useRef<HTMLInputElement>(null);
  const disabledShortcutsRef = useRef(false);

  useEffect(() => {
    if (!inputRef.current) {
      return;
    }

    inputRef.current.focus();
  }, []);

  const onCloseCallback = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!onClose) {
      return;
    }
    onClose();
  }, []);

  const onLinkCallback = (e?: any) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!onLink) {
      return;
    }

    const href = parseLink(value);
    if (href === '') {
      return;
    }

    onLink({ href, variables: linkVariables });
  };

  const onUnlinkCallback = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!onUnlink) {
        return;
      }
      onUnlink();
    },
    [value],
  );

  const onKeyDown = useMemo(
    () =>
      hotkeys({
        keyMap: {
          link: 'enter',
          close: 'escape',
        },
        handlers: {
          link: onLinkCallback,
          close: onCloseCallback,
        },
      }),
    [onLinkCallback, onCloseCallback],
  );

  const variablesLoaded = traits?.data && traits.data.length > 0;
  const insertVariable = useCallback(
    (newVariable: LinkVariable) => {
      setValue(`${value}{{${newVariable.id}}}`);

      setLinkVariables([...linkVariables, newVariable]);
    },
    [value, linkVariables],
  );

  const removeLinkVariable = useCallback(
    (id) => {
      setLinkVariables(linkVariables.filter((linkVariable) => linkVariable.id !== id));
    },
    [linkVariables],
  );

  const handleValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);

      // remove any link variables that do not appear within our value
      setLinkVariables(
        linkVariables.filter((linkVariable) => e.target.value.includes(`{{${linkVariable.id}}}`)),
      );
    },
    [value],
  );

  return (
    <EventListener
      target="document"
      onKeyDown={(event: any) => {
        if (disabledShortcutsRef.current) {
          return;
        }
        onKeyDown(event);
      }}
    >
      <Menu>
        <LinkInputContainer>
          <Input
            ref={inputRef}
            value={value}
            onChange={handleValueChange}
            placeholder="https://candu.ai"
            size={40}
          />
        </LinkInputContainer>
        <ButtonContainer>
          <Button appearance="text" variant="white" size="small" onClick={onUnlinkCallback}>
            Unlink
          </Button>
          <Button
            darkBackground
            appearance="outline"
            variant="secondary"
            size="small"
            ml="xs"
            onClick={onLinkCallback}
          >
            Link
          </Button>
        </ButtonContainer>
        <InputArea>
          {hasLinkVariables && canVariableAccess && !showingMore && (
            <MoreButton type="button" onClick={() => setShowingMore(true)}>
              Manage variables
            </MoreButton>
          )}
          {variablesLoaded && showingMore && (
            <ManageVariables
              traits={traits?.data || []}
              onClose={onLinkCallback}
              onSubmit={(e) => insertVariable(e)}
              linkVariables={linkVariables}
              removeLinkVariable={removeLinkVariable}
            />
          )}
        </InputArea>
      </Menu>
    </EventListener>
  );
};
